import { useWatch, type Control } from 'react-hook-form';

import { useLanguage } from '@/contexts/language-context';
import { fillTemplate } from '@/lib/utils';

import { formatDate, previewOccurrenceDates } from './helpers';
import type { RecurrenceFormValues } from './schema';

interface Props {
  control: Control<RecurrenceFormValues>;
  startDate: string;
}

const PREVIEW_LIMIT = 8;

// Sits under <RecurrenceFields> in the add-job form and shows the first few
// visit dates a series would generate, so a series reads as real rows rather
// than an abstract rule.
export function RecurrencePreview({ control, startDate }: Props) {
  const { t } = useLanguage();

  const repeats = useWatch({ control, name: 'repeats' });
  const recurrence = useWatch({ control, name: 'recurrence' });

  if (!repeats || !recurrence) return null;

  if (recurrence.frequency === 'custom') {
    return (
      <p className="text-xs text-muted-foreground" data-testid="recurrence-preview">
        {t.recurrencePreviewCustom}
      </p>
    );
  }

  if (
    recurrence.frequency === 'weekly' &&
    (recurrence.daysOfWeek?.length ?? 0) === 0
  ) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="recurrence-preview">
        {t.recurrencePreviewPickDays}
      </p>
    );
  }

  const dates = previewOccurrenceDates(startDate, recurrence, PREVIEW_LIMIT);
  if (dates.length === 0) return null;

  const heading =
    dates.length === 1
      ? t.recurrencePreviewCreatesOne
      : fillTemplate(t.recurrencePreviewCreates, { n: dates.length });
  const truncated =
    dates.length === PREVIEW_LIMIT && !recurrence.seriesEndDate;

  return (
    <div
      className="rounded-md border bg-muted/40 px-3 py-2 text-xs"
      data-testid="recurrence-preview"
    >
      <span className="font-medium">{heading}</span>{' '}
      <span className="text-muted-foreground">
        {dates.map((d) => formatDate(d)).join(', ')}
        {truncated
          ? ` … (${fillTemplate(t.recurrencePreviewFirstN, { n: PREVIEW_LIMIT })})`
          : ''}
      </span>
    </div>
  );
}
